import React, { useState, useRef, useEffect } from "react";
import classNames from "classnames";
import SVG from "components/base/SVG";
import css from "./style.scss";

const Select = ({
  lists = [],
  placeholder = "Select",
  onSelect,
  onCreate,
  selectedValue,
  valueKey = "id",
  labelKey = "name",
  name,
  inputStyle,
  dropdownStyle,
  inputBoxStyle,
  autoFlip = false,
}) => {
  const [query, setQuery] = useState("");
  const [filtered, setFiltered] = useState([]);
  const [isActionDropdownOpen, setIsActionDropdownOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [selectedItem, setSelectedItem] = useState(null);
  const [openUpward, setOpenUpward] = useState(false);

  const wrapperRef = useRef(null);

  const DROPDOWN_MAX_HEIGHT = 170; // matches .dropdown max-height

  const getScrollParent = (el) => {
    let node = el?.parentElement;
    while (node && node !== document.body) {
      const { overflowY } = window.getComputedStyle(node);
      if (overflowY === 'auto' || overflowY === 'scroll') return node;
      node = node.parentElement;
    }
    return null;
  };

  const updateDropdownDirection = () => {
    if (!autoFlip || !wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const scrollParent = getScrollParent(wrapperRef.current);
    const parentRect = scrollParent?.getBoundingClientRect();
    const boundsTop = parentRect ? Math.max(parentRect.top, 0) : 0;
    const boundsBottom = parentRect ? Math.min(parentRect.bottom, window.innerHeight) : window.innerHeight;

    const spaceBelow = boundsBottom - rect.bottom;
    const spaceAbove = rect.top - boundsTop;

    setOpenUpward(spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow);
  };

  const getSortedLists = (data) =>
    [...data].sort((a, b) =>
      (a?.[labelKey] || "").localeCompare(
        b?.[labelKey] || "",
        "en",
        { sensitivity: "base" }
      )
    );

  useEffect(() => {
    setFiltered(getSortedLists(lists));
  }, [lists, labelKey]);

  useEffect(() => {
    if (
      selectedValue === undefined ||
      selectedValue === null ||
      !lists.length
    ) {
      setSelectedItem(null);
      setQuery("");
      return;
    }

    const found = lists.find(
      (item) => item[valueKey] === selectedValue
    );

    if (found) {
      setSelectedItem(found);
      setQuery(found[labelKey]);
    }
  }, [selectedValue, lists, valueKey, labelKey]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target)
      ) {
        setIsActionDropdownOpen(false);
        setActiveIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInput = (value) => {
    setQuery(value);
    setIsActionDropdownOpen(true);
    updateDropdownDirection();

    if (!value.trim()) {
      setSelectedItem(null);
      setFiltered(getSortedLists(lists));

      onSelect?.({
        name,
        value: "",
      });

      return;
    }

    if (selectedItem) {
      setSelectedItem(null);
    }

    const filteredLists = lists.filter((item) =>
      (item[labelKey] || "")
        .toLowerCase()
        .includes(value.toLowerCase())
    );

    setFiltered(filteredLists);
  };

  const handleSelect = (item) => {
    setQuery(item[labelKey]);
    setSelectedItem(item);
    setIsActionDropdownOpen(false);

    onSelect?.({
      name,
      value: item[valueKey],
    });

    setFiltered(getSortedLists(lists));
  };

  const handleCreate = () => {
    if (!query.trim()) return;

    const exists = lists.some(
      (item) =>
        (item[labelKey] || "").toLowerCase() ===
        query.toLowerCase()
    );

    if (!exists && onCreate) {
      onCreate(query);
      setIsActionDropdownOpen(false);
    }
  };

  const handleKeyDown = (e) => {
    if (!isActionDropdownOpen) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) =>
        Math.min(prev + 1, filtered.length)
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();

      if (activeIndex === filtered.length) {
        handleCreate();
      } else if (activeIndex >= 0) {
        handleSelect(filtered[activeIndex]);
      }
    }
  };

  return (
    <div className={classNames(css.wrapper, dropdownStyle)} ref={wrapperRef}>
      <div
        className={classNames(css.inputBox, inputBoxStyle, {
          [css.open]: isActionDropdownOpen,
          [css.openUp]: isActionDropdownOpen && openUpward,
        })}
        onClick={() => {
          setIsActionDropdownOpen(true);
          updateDropdownDirection();
          setFiltered(getSortedLists(lists));
        }}
      >
        <input
          type="text"
          value={query}
          placeholder={placeholder}
          className={css.input}
          style={inputStyle} 
          onChange={(e) => handleInput(e.target.value)}
          onFocus={() => {
            setIsActionDropdownOpen(true);
            updateDropdownDirection();
          }}
          onKeyDown={handleKeyDown}
        />

        <SVG
          icon="iconCaretDown"
          className={css.dropdownCaretIcon}
        />
      </div>

      {isActionDropdownOpen && (
        <ul className={classNames(css.dropdown, { [css.dropUp]: openUpward })}>
          {filtered.length > 0 ? (
            filtered.map((item, index) => (
              <li
                key={item[valueKey]}
                className={classNames(css.item, {
                  [css.active]: index === activeIndex,
                  [css.selected]:
                    selectedItem?.[valueKey] === item[valueKey],
                })}
                onClick={() => handleSelect(item)}
              >
                {item[labelKey]}
              </li>
            ))
          ) : (
            <li className={css.noMatch}>No Matches</li>
          )}
        </ul>
      )}
    </div>
  );
};

export default Select;